import React, { useEffect, useRef, useState } from 'react';
import { Box, Alert as JoyAlert, CircularProgress } from '@mui/joy';
import type { StormTerritory } from '../../types/stormtracker';

interface TerritoryMapProps {
  territory?: StormTerritory;
  height?: string | number;
  editable?: boolean;
  onCenterChange?: (lat: number, lng: number) => void;
  onRadiusChange?: (radius: number) => void;
  centerLat?: number;
  centerLng?: number;
  radiusMiles?: number;
}

const MILES_TO_METERS = 1609.34;

export const TerritoryMap: React.FC<TerritoryMapProps> = ({
  territory,
  height = 300,
  editable = false,
  onCenterChange,
  onRadiusChange,
  centerLat,
  centerLng,
  radiusMiles,
}) => {
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapInstanceRef = useRef<google.maps.Map | null>(null);
  const markerRef = useRef<google.maps.Marker | null>(null);
  const circleRef = useRef<google.maps.Circle | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Store callback refs to avoid stale closures
  const onCenterChangeRef = useRef(onCenterChange);
  const onRadiusChangeRef = useRef(onRadiusChange);
  onCenterChangeRef.current = onCenterChange;
  onRadiusChangeRef.current = onRadiusChange;

  // Determine center point
  const lat = centerLat ?? territory?.center_latitude ?? 32.7767; // Dallas default
  const lng = centerLng ?? territory?.center_longitude ?? -96.797;
  const radius = radiusMiles ?? territory?.radius_miles ?? 25;
  const territoryName = territory?.name || 'Territory Center';

  // Initialize map once on mount
  useEffect(() => {
    if (!mapContainerRef.current) return;

    let isMounted = true;

    const initMap = () => {
      if (!isMounted || !mapContainerRef.current) return;

      if (!window.google || !window.google.maps) {
        setTimeout(initMap, 500);
        return;
      }

      try {
        const position = { lat, lng };

        const map = new google.maps.Map(mapContainerRef.current, {
          center: position,
          zoom: 9,
          mapTypeId: google.maps.MapTypeId.ROADMAP,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: true,
          zoomControl: true,
        });

        mapInstanceRef.current = map;

        // Add center marker
        const marker = new google.maps.Marker({
          position,
          map,
          draggable: editable,
          title: territoryName,
          icon: {
            path: google.maps.SymbolPath.CIRCLE,
            scale: 10,
            fillColor: '#1976d2',
            fillOpacity: 0.9,
            strokeColor: '#ffffff',
            strokeWeight: 2,
          },
        });

        markerRef.current = marker;

        // Add radius circle
        const circle = new google.maps.Circle({
          map,
          center: position,
          radius: radius * MILES_TO_METERS,
          fillColor: '#1976d2',
          fillOpacity: 0.1,
          strokeColor: '#1976d2',
          strokeOpacity: 0.8,
          strokeWeight: 2,
          editable: editable,
        });

        circleRef.current = circle;

        // Fit bounds to show the entire circle
        const bounds = circle.getBounds();
        if (bounds) {
          map.fitBounds(bounds);
        }

        // Handle marker drag (for editable mode)
        if (editable) {
          marker.addListener('dragend', () => {
            const newPosition = marker.getPosition();
            if (newPosition) {
              circle.setCenter(newPosition);
              onCenterChangeRef.current?.(newPosition.lat(), newPosition.lng());
            }
          });
        }

        // Handle radius change (for editable mode)
        if (editable) {
          circle.addListener('radius_changed', () => {
            const newRadiusMeters = circle.getRadius();
            const newRadiusMiles = newRadiusMeters / MILES_TO_METERS;
            onRadiusChangeRef.current?.(Math.round(newRadiusMiles));
          });
        }

        // Handle click on map to set center (for editable mode)
        if (editable) {
          map.addListener('click', (event: google.maps.MapMouseEvent) => {
            if (event.latLng) {
              marker.setPosition(event.latLng);
              circle.setCenter(event.latLng);
              onCenterChangeRef.current?.(event.latLng.lat(), event.latLng.lng());
            }
          });
        }

        setLoading(false);
      } catch (err) {
        console.error('Error initializing map:', err);
        setError('Failed to load map');
        setLoading(false);
      }
    };

    initMap();

    return () => {
      isMounted = false;
      if (markerRef.current) {
        markerRef.current.setMap(null);
      }
      if (circleRef.current) {
        circleRef.current.setMap(null);
      }
    };
  }, []); // Only run on mount - subsequent updates handled by second useEffect

  // Update circle when props change
  useEffect(() => {
    if (markerRef.current && circleRef.current && mapInstanceRef.current) {
      const position = { lat, lng };
      markerRef.current.setPosition(position);
      circleRef.current.setCenter(position);
      circleRef.current.setRadius(radius * MILES_TO_METERS);

      // Fit bounds to show the entire circle
      const bounds = circleRef.current.getBounds();
      if (bounds) {
        mapInstanceRef.current.fitBounds(bounds);
      }
    }
  }, [lat, lng, radius]);

  if (error) {
    return (
      <JoyAlert color="danger" sx={{ m: 2 }}>
        {error}
      </JoyAlert>
    );
  }

  return (
    <Box sx={{ position: 'relative', height, borderRadius: 'md', overflow: 'hidden' }}>
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'background.level1',
            zIndex: 1,
          }}
        >
          <CircularProgress size="md" />
        </Box>
      )}
      <Box ref={mapContainerRef} sx={{ width: '100%', height: '100%' }} />
    </Box>
  );
};

export default TerritoryMap;
